/*
 * CreateEditIntakeView — thin orchestrator for the shared create AND edit intake-form
 * flow (carved from the former 1231-line JSX giant). It owns ALL form state, the ~18
 * question-mutation callbacks, the validation cascade (onSaveBtnPress) and the request
 * mapping (generateCorrectIntakeFormRequestType / generateIntakeFormRequestQuestionObject).
 * Layout/markup is delegated to co-located typed section components.
 *
 * Behavior is byte-equivalent to the JSX original — only the structure (carve) and the
 * inline breadcrumb/scss are new (shared <Breadcrumb>, Tailwind + DS tokens).
 *
 * SCSS → Tailwind mapping (ceiv-container; the dead .create-new-intake-form-* classes dropped):
 *   .ceiv-container: padding:32px 24px 100px / flex / flex-direction:column / gap:24px /
 *     background:--surface-secondary(#f6f7f8) / min-height:100%
 *     → pt-sp-4 px-sp-3 pb-[100px] flex flex-col gap-sp-3 bg-deep-navy-5 min-h-full
 *   (carved-component class mappings are documented in each component file)
 *
 * Arbitrary value: pb-[100px] (100px bottom padding, clears the fixed action bar; no DS token).
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { errorCodes, getUserFriendlyErrorMessage } from '../../../../../utils/errormessages'
import {
  INTAKE_FORM_NEW_QUESTION_MULTIPLE_ANSWERS_CHECKBOX_TEXT,
  INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_TEXT,
  INTAKE_FORM_TYPE_MULTIPLE_CHOICE_REQUEST_TEXT,
  INTAKE_FORM_TYPE_MULTIPLE_CHOICE_TEXT,
  INTAKE_FORM_TYPE_STATEMENT_REQUEST_TEXT,
  INTAKE_FORM_TYPE_STATEMENT_TEXT,
  INTAKE_FORM_TYPE_TEXT_BOX_TEXT,
  INTAKE_INFO_NAME_MAX_LENGTH,
  FILE_UPLOAD_DOCUMENT_TYPE_TEXT,
  FILE_UPLOAD_IMAGE_FILE_TYPE_TEXT,
  FILE_UPLOAD_DOCUMENT_ALLOWED_FORMATS,
  FILE_UPLOAD_IMAGE_ALLOWED_FORMATS,
  INTAKE_FORM_TYPE_TEXT_BOX_REQUEST_TEXT,
  INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_REQUEST_TEXT,
  INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_REQUEST_DOCUMENT_TEXT,
  INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_REQUEST_IMAGE_TEXT,
  INTAKE_FORM_TYPE_PHONE_NUMBER_TEXT,
  INTAKE_FORM_TYPE_DATE_TEXT,
  INTAKE_FORM_TYPE_EMAIL_TEXT,
  INTAKE_FORM_TYPE_EMAIL_REQUEST_TEXT,
  INTAKE_FORM_TYPE_DATE_REQUEST_TEXT,
  INTAKE_FORM_TYPE_PHONE_NUMBER_REQUEST_TEXT,
  INTAKE_FORM_TYPE_SINGLE_TEXT,
  INTAKE_FORM_TYPE_HEIGHT_WEIGHT_TEXT,
  INTAKE_FORM_TYPE_PREFERRED_PHARMACY_TEXT,
  INTAKE_FORM_PREFERRED_PHARMACY_QUESTION,
} from '../../../../../utils/constants'
import { IS_REQUIRED_CHECKBOX_TYPE } from '../../Constants'
import { updateQuestionsOnRequiredCheckboxChange } from './utils'
import type {
  CreateEditIntakeViewProps,
  FileFormatOption,
  IntakeFormRequestBody,
  IntakeQuestionBuilder,
  QuestionOption,
} from './types'
import { IntakeBreadcrumbHeader } from './components/IntakeBreadcrumbHeader'
import { IntakeInfoBanner } from './components/IntakeInfoBanner'
import { IntakeNameCard } from './components/IntakeNameCard'
import { QuestionBuildersList } from './components/QuestionBuildersList'
import { IntakeDeleteSection } from './components/IntakeDeleteSection'
import { IntakeActionBar } from './components/IntakeActionBar'
import { DiscardChangesPopup } from '@/components/discard-changes-popup/DiscardChangesPopup'
import { useDiscardChangesGuard } from '@/components/discard-changes-popup/useDiscardChangesGuard'

const noop = () => undefined

// Client-side ids for builders/options + React keys use crypto.randomUUID() — the codebase
// TSX convention (IntakeFormViewMode / new-members / viewservice). Replaces the former JSX
// `uuid` import (uuid@8 ships no TS types). Behavior-equivalent.

const CreateEditIntakeView = memo(
  ({
    isEdit,
    title,
    questions,
    error,
    createNewIntakeForm,
    onCancelBtnPress,
    setIntakeFormError,
    saveIntakeForm,
    onDeleteIntakeForm,
  }: CreateEditIntakeViewProps) => {
    const setFormError = setIntakeFormError ?? noop
    const [newIntakeFormNameValue, setNewIntakeFormNameValue] = useState(title || '')
    const [newIntakeFormNameError, setNewIntakeFormNameError] = useState(false)
    const [newIntakeFormNameLocalError, setNewIntakeFormNameLocalError] = useState('')

    const NEW_INTAKE_FORM_FILE_UPLOADS_TYPES = [
      {
        name: FILE_UPLOAD_DOCUMENT_TYPE_TEXT,
        allowedFormats: FILE_UPLOAD_DOCUMENT_ALLOWED_FORMATS,
      },
      {
        name: FILE_UPLOAD_IMAGE_FILE_TYPE_TEXT,
        allowedFormats: FILE_UPLOAD_IMAGE_ALLOWED_FORMATS,
      },
    ]
    const multiSelectFileFormatOptions: FileFormatOption[] = NEW_INTAKE_FORM_FILE_UPLOADS_TYPES.map((t) => ({
      value: t.name,
      allowedFormats: t.allowedFormats,
      isActive: false,
    }))

    const [intakeQuestionBuildersList, setIntakeQuestionBuildersList] = useState<IntakeQuestionBuilder[]>(
      questions || [
        {
          id: crypto.randomUUID(),
          questionTypeValue: '',
          fileFormatOptions: multiSelectFileFormatOptions,
          fileFormatError: false,
          fileFormatLocalError: '',
          isPreviouslySaved: false,
          previousSavedData: {
            options: [
              { optionId: crypto.randomUUID(), optionValue: '', optionLocalError: '', isOptionError: false },
              { optionId: crypto.randomUUID(), optionValue: '', optionLocalError: '', isOptionError: false },
            ],
            message: '',
            questionTypeValue: '',
          },
          message: '',
          checkboxesValues: {
            EnableMultipleAnswers: false,
            isRequired: false,
          },
          options: [
            {
              optionId: crypto.randomUUID(),
              optionValue: '',
              optionLocalError: '',
              isOptionError: false,
              isFirstTimeSaveClicked: false,
            },
            {
              optionId: crypto.randomUUID(),
              optionValue: '',
              optionLocalError: '',
              isOptionError: false,
              isFirstTimeSaveClicked: false,
            },
          ],
          questionTypeError: false,
          questionTypeLocalError: '',
          questionMessageValueError: false,
          questionMessageValueLocalError: '',
          isSaved: false,
        },
      ],
    )

    const isAllBuildersClosed = intakeQuestionBuildersList.every((t) => t.isSaved)

    const isAllQuestionTypeDropdownsFilled = intakeQuestionBuildersList.every(
      (t) => (t.questionTypeValue?.length ?? 0) > 0,
    )

    const isAllMessageFieldFilled = intakeQuestionBuildersList.every((t) => (t.message?.trim().length ?? 0) > 0)

    const isFileFormatFieldError = useCallback(
      () =>
        intakeQuestionBuildersList
          .filter((t) => t.questionTypeValue === INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_TEXT)
          .filter((builder) => builder.fileFormatOptions.every((opt) => !opt.isActive)).length > 0,
      [intakeQuestionBuildersList],
    )
    const isFileFormatDropDownEmptyByBuilderId = (builderId: string) => {
      const objectWithSaveRequest = intakeQuestionBuildersList.find((t) => t.id === builderId)

      return (
        objectWithSaveRequest?.questionTypeValue === INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_TEXT &&
        objectWithSaveRequest.fileFormatOptions.every((opt) => !opt.isActive)
      )
    }

    const isQuestionListEmpty = intakeQuestionBuildersList?.length === 0

    const isAllOptionsFilled = useCallback(() => {
      const questionsOptions = intakeQuestionBuildersList
        .map((t) => (t.questionTypeValue === INTAKE_FORM_TYPE_MULTIPLE_CHOICE_TEXT ? t.options : ''))
        .filter((t): t is QuestionOption[] => t !== '')
      return questionsOptions.every((t) => t?.every((p) => p.optionValue.trim()?.length !== 0))
    }, [intakeQuestionBuildersList])

    const generateCorrectIntakeFormRequestType = (typeFromDropDown: string): string | undefined => {
      switch (typeFromDropDown) {
        case INTAKE_FORM_TYPE_STATEMENT_TEXT:
          return INTAKE_FORM_TYPE_STATEMENT_REQUEST_TEXT
        case INTAKE_FORM_TYPE_TEXT_BOX_TEXT:
          return INTAKE_FORM_TYPE_TEXT_BOX_REQUEST_TEXT
        case INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_TEXT:
          return INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_REQUEST_TEXT
        case INTAKE_FORM_TYPE_MULTIPLE_CHOICE_TEXT:
          return INTAKE_FORM_TYPE_MULTIPLE_CHOICE_REQUEST_TEXT
        case INTAKE_FORM_TYPE_PHONE_NUMBER_TEXT:
          return INTAKE_FORM_TYPE_PHONE_NUMBER_REQUEST_TEXT
        case INTAKE_FORM_TYPE_DATE_TEXT:
          return INTAKE_FORM_TYPE_DATE_REQUEST_TEXT
        case INTAKE_FORM_TYPE_EMAIL_TEXT:
          return INTAKE_FORM_TYPE_EMAIL_REQUEST_TEXT
        default:
          return undefined
      }
    }

    const generateFileFormatsForQuestionRequest = (fileFormatOptionsArray: FileFormatOption[]): string[] => {
      const isSelectedOptionsArray = fileFormatOptionsArray.filter((t) => t.isActive)
      switch (isSelectedOptionsArray?.length) {
        case 0:
          return []
        case 2:
          return [
            INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_REQUEST_DOCUMENT_TEXT,
            INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_REQUEST_IMAGE_TEXT,
          ]
        case 1:
          return isSelectedOptionsArray[0]?.value === FILE_UPLOAD_DOCUMENT_TYPE_TEXT ?
              [INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_REQUEST_DOCUMENT_TEXT]
            : [INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_REQUEST_IMAGE_TEXT]
        default:
          return []
      }
    }

    const generateIntakeFormRequestQuestionObject = useCallback(
      (question: IntakeQuestionBuilder): Record<string, unknown> | undefined => {
        switch (question.questionTypeValue) {
          case INTAKE_FORM_TYPE_STATEMENT_TEXT:
            return {
              Type: generateCorrectIntakeFormRequestType(question.questionTypeValue),
              Question: question.message?.trim(),
              IntakeFormQuestionId: question.IntakeFormQuestionId,
            }

          case INTAKE_FORM_TYPE_SINGLE_TEXT:
            return {
              Type: 'condition',
              Question: question.message?.trim() || '',
              IsRequired: question.checkboxesValues.isRequired,
              IntakeFormQuestionId: question.IntakeFormQuestionId,
              Options: question.options.map((opt) => {
                const followUpInput = opt.hasFollowUp ? { IsRequired: !!opt.followUpRequired } : null

                return {
                  Name: opt.optionValue || '',
                  Input: followUpInput,
                }
              }),
            }

          case INTAKE_FORM_TYPE_TEXT_BOX_TEXT:
          case INTAKE_FORM_TYPE_PHONE_NUMBER_TEXT:
          case INTAKE_FORM_TYPE_DATE_TEXT:
          case INTAKE_FORM_TYPE_EMAIL_TEXT:
            return {
              Type: generateCorrectIntakeFormRequestType(question.questionTypeValue),
              Question: question.message?.trim(),
              IsRequired: question.checkboxesValues.isRequired,
              IntakeFormQuestionId: question.IntakeFormQuestionId,
            }

          case INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_TEXT:
            return {
              Type: generateCorrectIntakeFormRequestType(question.questionTypeValue),
              Question: question.message?.trim(),
              FileTypes: generateFileFormatsForQuestionRequest(question.fileFormatOptions),
              IsRequired: question.checkboxesValues.isRequired,
              IntakeFormQuestionId: question.IntakeFormQuestionId,
            }

          case INTAKE_FORM_TYPE_MULTIPLE_CHOICE_TEXT:
            return {
              Type: generateCorrectIntakeFormRequestType(question.questionTypeValue),
              Question: question.message?.trim(),
              IsMultipleAnswerAllowed: question.checkboxesValues.EnableMultipleAnswers,
              IsRequired: question.checkboxesValues.isRequired,
              Options: question.options.map((t) => t.optionValue),
              IntakeFormQuestionId: question.IntakeFormQuestionId,
            }

          case INTAKE_FORM_TYPE_HEIGHT_WEIGHT_TEXT:
            return {
              Type: 'bodymeasurement',
              IsRequired: question.checkboxesValues.isRequired,

              IntakeFormQuestionId: question.IntakeFormQuestionId,
            }

          case INTAKE_FORM_TYPE_PREFERRED_PHARMACY_TEXT:
            return {
              Type: 'pharmacyselection',
              IsRequired: question.checkboxesValues.isRequired,
              IntakeFormQuestionId: question.IntakeFormQuestionId,
            }

          default:
            return undefined
        }
      },
      [],
    )

    const optionLocalErrorMsgHandler = (option: QuestionOption): string =>
      (
        option.optionLocalError === getUserFriendlyErrorMessage(errorCodes.DUPLICATE_OPTIONS) &&
        !option.isFirstTimeSaveClicked
      ) ?
        getUserFriendlyErrorMessage(errorCodes.DUPLICATE_OPTIONS)
      : getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Option')

    const onSaveBtnPress = useCallback(() => {
      if (
        newIntakeFormNameValue?.trim()?.length === 0 ||
        newIntakeFormNameValue?.length > INTAKE_INFO_NAME_MAX_LENGTH ||
        !isAllOptionsFilled() ||
        !isAllBuildersClosed ||
        !isAllMessageFieldFilled ||
        isFileFormatFieldError() ||
        isQuestionListEmpty
      ) {
        let allIntakeFormsListCopy = [...intakeQuestionBuildersList]

        if (isQuestionListEmpty) {
          setFormError(getUserFriendlyErrorMessage(errorCodes.NEED_AT_LEAST_ONE_QUESTION))
        }
        if (!isAllBuildersClosed) {
          allIntakeFormsListCopy = allIntakeFormsListCopy.map((t) =>
            !t.isSaved ? { ...t, questionTypeError: true } : t,
          )
          setFormError(getUserFriendlyErrorMessage(errorCodes.NEED_SAVE_AT_LEAST_ONE_QUESTION))
        }
        if (isFileFormatFieldError()) {
          allIntakeFormsListCopy = allIntakeFormsListCopy.map((t) =>
            t.fileFormatOptions.every((opt) => !opt.isActive) ?
              {
                ...t,
                fileFormatError: true,
                fileFormatLocalError: getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'File format(s)'),
              }
            : t,
          )
          setFormError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'File format(s)'))
        }
        if (!isAllQuestionTypeDropdownsFilled) {
          allIntakeFormsListCopy = allIntakeFormsListCopy.map((t) =>
            t.questionTypeValue?.length === 0 ?
              {
                ...t,
                questionTypeError: true,
                questionTypeLocalError: getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Question type'),
              }
            : t,
          )
          setFormError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Question type'))
        }
        if (!isAllOptionsFilled()) {
          allIntakeFormsListCopy = allIntakeFormsListCopy.map((t) =>
            t.questionTypeValue === INTAKE_FORM_TYPE_MULTIPLE_CHOICE_TEXT ?
              {
                ...t,
                options: t.options.map((option) =>
                  option.optionValue?.trim()?.length === 0 ?
                    {
                      ...option,
                      optionLocalError: optionLocalErrorMsgHandler(option),
                      isOptionError: true,
                      isFirstTimeSaveClicked: true,
                    }
                  : option,
                ),
              }
            : t,
          )
          setFormError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Option'))
        }
        if (!isAllMessageFieldFilled) {
          allIntakeFormsListCopy = allIntakeFormsListCopy.map((t) =>
            t.message?.trim().length === 0 ?
              t.questionTypeValue?.length !== 0 ?
                {
                  ...t,
                  questionMessageValueError: true,
                  questionMessageValueLocalError: getUserFriendlyErrorMessage(
                    errorCodes.FIELD_REQUIRED,
                    null,
                    'Question',
                  ),
                }
              : t
            : t,
          )

          setFormError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Question'))
        }
        if (newIntakeFormNameValue?.trim()?.length === 0) {
          setNewIntakeFormNameError(true)
          setNewIntakeFormNameLocalError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Intake name'))
          setFormError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Intake name'))
        }
        if (newIntakeFormNameValue?.length > INTAKE_INFO_NAME_MAX_LENGTH) {
          setNewIntakeFormNameError(true)
          setNewIntakeFormNameLocalError(getUserFriendlyErrorMessage(errorCodes.MAX_CHARACTER_LIMIT))
          setFormError(getUserFriendlyErrorMessage(errorCodes.MAX_CHARACTER_LIMIT))
        }
        setIntakeQuestionBuildersList(allIntakeFormsListCopy)
        return
      }

      const newIntakeFormRequestBody: IntakeFormRequestBody = {
        Name: newIntakeFormNameValue,
        Questions: intakeQuestionBuildersList.map((t) => generateIntakeFormRequestQuestionObject(t)),
      }
      if (isEdit) {
        saveIntakeForm?.(newIntakeFormRequestBody)
      } else {
        createNewIntakeForm?.(newIntakeFormRequestBody)
      }
    }, [
      newIntakeFormNameValue,
      isAllOptionsFilled,
      isAllBuildersClosed,
      isAllMessageFieldFilled,
      isFileFormatFieldError,
      isQuestionListEmpty,
      intakeQuestionBuildersList,
      isAllQuestionTypeDropdownsFilled,
      generateIntakeFormRequestQuestionObject,
      isEdit,
      saveIntakeForm,
      createNewIntakeForm,
      setFormError,
    ])

    const onToggleFollowUp = (builderId: string, optionId: string) => {
      setIntakeQuestionBuildersList((prev) =>
        prev.map((q) =>
          q.id === builderId ?
            {
              ...q,
              options: q.options.map((option) =>
                option.optionId === optionId ?
                  {
                    ...option,
                    hasFollowUp: !option.hasFollowUp,
                  }
                : option,
              ),
            }
          : q,
        ),
      )
    }

    const onChangeFollowUpText = (builderId: string, optionId: string, value: string) => {
      setIntakeQuestionBuildersList((prev) =>
        prev.map((q) =>
          q.id === builderId ?
            {
              ...q,
              options: q.options.map((option) =>
                option.optionId === optionId ? { ...option, followUpText: value } : option,
              ),
            }
          : q,
        ),
      )
    }

    const onChangeFollowUpRequired = (builderId: string, optionId: string, isRequired: boolean) => {
      setIntakeQuestionBuildersList((prev) =>
        prev.map((q) =>
          q.id === builderId ?
            {
              ...q,
              options: q.options.map((option) =>
                option.optionId === optionId ? { ...option, followUpRequired: isRequired } : option,
              ),
            }
          : q,
        ),
      )
    }

    const onChangeQuestionTypeValue = useCallback(
      (questionBuilderId: string, value: string) => {
        const newIntakeQuestionBuildersList = intakeQuestionBuildersList.map((t) =>
          t.id === questionBuilderId ?
            value !== t.questionTypeValue ?
              {
                ...t,
                questionTypeValue: value,
                fileFormatOptions: multiSelectFileFormatOptions,
                fileFormatError: false,
                fileFormatLocalError: '',
                message: isEdit ? t.message || '' : '',
                questionTypeError: false,
                questionTypeLocalError: '',
                questionMessageValueError: false,
                questionMessageValueLocalError: '',
                checkboxesValues: {
                  EnableMultipleAnswers: false,
                  isRequired: false,
                },
                options:
                  value === INTAKE_FORM_TYPE_SINGLE_TEXT ?
                    [
                      {
                        optionId: crypto.randomUUID(),
                        optionValue: 'Yes',
                        hasFollowUp: false,
                        followUpText: '',
                        followUpRequired: false,
                      },
                      {
                        optionId: crypto.randomUUID(),
                        optionValue: 'No',
                        hasFollowUp: false,
                        followUpText: '',
                        followUpRequired: false,
                      },
                    ]
                  : [
                      {
                        optionId: crypto.randomUUID(),
                        optionValue: '',
                        optionLocalError: '',
                        isOptionError: false,
                      },
                      {
                        optionId: crypto.randomUUID(),
                        optionValue: '',
                        optionLocalError: '',
                        isOptionError: false,
                      },
                    ],
              }
            : t
          : t,
        )

        setFormError('')
        setIntakeQuestionBuildersList(newIntakeQuestionBuildersList)
      },
      [intakeQuestionBuildersList, multiSelectFileFormatOptions, isEdit, setFormError],
    )

    const onChangeFileFormatValue = useCallback(
      (builderId: string, value: FileFormatOption[]) => {
        const newIntakeQuestionBuildersList = intakeQuestionBuildersList.map((t) =>
          t.id === builderId ? { ...t, fileFormatOptions: value, fileFormatError: false, fileFormatLocalError: '' } : t,
        )
        setFormError('')
        setIntakeQuestionBuildersList(newIntakeQuestionBuildersList)
      },
      [intakeQuestionBuildersList, setFormError],
    )

    const onChangeCheckBoxesValue = useCallback(
      (questionBuilderId: string, checkBoxType: string, value: boolean) => {
        let newIntakeQuestionBuildersList: IntakeQuestionBuilder[] = []
        switch (checkBoxType) {
          case INTAKE_FORM_NEW_QUESTION_MULTIPLE_ANSWERS_CHECKBOX_TEXT:
            newIntakeQuestionBuildersList = intakeQuestionBuildersList.map((t) =>
              t.id === questionBuilderId ?
                { ...t, checkboxesValues: { ...t.checkboxesValues, EnableMultipleAnswers: value } }
              : t,
            )
            break
          case IS_REQUIRED_CHECKBOX_TYPE:
            newIntakeQuestionBuildersList = updateQuestionsOnRequiredCheckboxChange(
              intakeQuestionBuildersList,
              questionBuilderId,
              value,
            )
            break
          default:
            break
        }

        setFormError('')
        setIntakeQuestionBuildersList(newIntakeQuestionBuildersList)
      },
      [intakeQuestionBuildersList, setFormError],
    )

    const onChangeQuestionOptionValue = useCallback(
      (questionBuilderId: string, optionId: string, value: string) => {
        const newIntakeQuestionBuildersList = intakeQuestionBuildersList.map((t) =>
          t.id === questionBuilderId ?
            {
              ...t,
              questionTypeError: false,
              options: t.options.map((opt) =>
                opt.optionId === optionId ?
                  {
                    ...opt,
                    optionValue: value,
                    optionLocalError: '',
                    isOptionError: false,
                    isFirstTimeSaveClicked: false,
                  }
                : opt,
              ),
            }
          : t,
        )
        setFormError('')
        setIntakeQuestionBuildersList(newIntakeQuestionBuildersList)
      },
      [intakeQuestionBuildersList, setFormError],
    )

    const getDuplicates = (array: QuestionOption[]): string[] => {
      const values = array.map((a) => a.optionValue)
      const uniqueValues = new Set(values)

      return array
        .map((item) => {
          if (uniqueValues.has(item.optionValue)) {
            uniqueValues.delete(item.optionValue)
            return undefined
          }
          return item.optionId
        })
        .filter((id): id is string => Boolean(id))
    }

    const onChangeQuestionOptionErrorValue = useCallback(
      (questionBuilderId: string, _optionId: string, errorText: string, isClearOptionValue?: boolean) => {
        let hasDuplicateError = false
        const newIntakeQuestionBuildersList = intakeQuestionBuildersList.map((intakeQuestion) => {
          if (intakeQuestion.id === questionBuilderId) {
            const duplicates = getDuplicates(intakeQuestion.options)

            return {
              ...intakeQuestion,
              options: intakeQuestion.options.map((option) => {
                const hasDoubles = duplicates.includes(option.optionId)

                const optionError =
                  hasDoubles && option.optionValue ? getUserFriendlyErrorMessage(errorCodes.DUPLICATE_OPTIONS) : ''

                if (optionError) {
                  hasDuplicateError = true
                }

                return {
                  ...option,
                  optionValue: isClearOptionValue ? '' : option.optionValue,
                  optionLocalError: optionError,
                  isOptionError: !!optionError,
                }
              }),
            }
          }
          return intakeQuestion
        })
        // Only surface the duplicate-options error when a real duplicate actually exists.
        // Previously this fired `errorText` unconditionally on every option blur (behavior
        // carried over verbatim from develop), popping a spurious "Cannot use duplicate
        // options." toast while typing unique options (e.g. "1" then "2").
        setFormError(hasDuplicateError ? errorText : '')
        setIntakeQuestionBuildersList(newIntakeQuestionBuildersList)
      },
      [intakeQuestionBuildersList, setFormError],
    )

    const onChangeQuestionMessageValue = useCallback(
      (questionBuilderId: string, value: string) => {
        const newIntakeQuestionBuildersList = intakeQuestionBuildersList.map((t) =>
          t.id === questionBuilderId ?
            {
              ...t,
              message: value,
              questionMessageValueError: false,
              questionTypeError: false,
              questionMessageValueLocalError: '',
            }
          : t,
        )
        setFormError('')
        setIntakeQuestionBuildersList(newIntakeQuestionBuildersList)
      },
      [intakeQuestionBuildersList, setFormError],
    )

    const onBuilderCancelBtnPress = useCallback(
      (questionBuilderId: string) => {
        const newIntakeQuestionBuildersList = intakeQuestionBuildersList.filter((t) => t.id !== questionBuilderId)
        setIntakeQuestionBuildersList(newIntakeQuestionBuildersList)
      },
      [intakeQuestionBuildersList],
    )

    const onBuilderSaveBtnPress = useCallback(
      (
        questionTypeValue: string,
        questionMessage: string,
        builderId: string,
        questionOptionsList: QuestionOption[],
      ) => {
        let message = questionMessage?.trim()

        if (questionTypeValue === INTAKE_FORM_TYPE_HEIGHT_WEIGHT_TEXT && !message) {
          message = 'Height & Weight question'
        }
        if (questionTypeValue === INTAKE_FORM_TYPE_PREFERRED_PHARMACY_TEXT && !message) {
          message = INTAKE_FORM_PREFERRED_PHARMACY_QUESTION
        }

        let questionsListCopy = [...intakeQuestionBuildersList]

        const hasValidOptions =
          questionOptionsList.every((option) => option?.optionValue.trim()?.length !== 0) &&
          questionOptionsList.length ===
            new Set(questionOptionsList.map((option) => option.optionValue.toLowerCase())).size

        const isQuestionMessageEmpty =
          questionTypeValue !== INTAKE_FORM_TYPE_HEIGHT_WEIGHT_TEXT && message?.trim()?.length === 0

        if (
          questionTypeValue?.trim()?.length === 0 ||
          isQuestionMessageEmpty ||
          (!hasValidOptions &&
            (questionTypeValue === INTAKE_FORM_TYPE_MULTIPLE_CHOICE_TEXT ||
              questionTypeValue === INTAKE_FORM_TYPE_SINGLE_TEXT)) ||
          isFileFormatDropDownEmptyByBuilderId(builderId)
        ) {
          if (isFileFormatDropDownEmptyByBuilderId(builderId)) {
            questionsListCopy = questionsListCopy.map((t) =>
              t.id === builderId ?
                {
                  ...t,
                  fileFormatError: true,
                  fileFormatLocalError: getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'File format(s)'),
                }
              : t,
            )
          }

          // If questionType was missing
          if (questionTypeValue?.trim()?.length === 0) {
            questionsListCopy = questionsListCopy.map((t) =>
              t.id === builderId ?
                {
                  ...t,
                  questionTypeError: true,
                  questionTypeLocalError: getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Question type'),
                }
              : t,
            )
          }

          // If questionMessage is empty and not "Height & Weight"
          if (isQuestionMessageEmpty && questionTypeValue?.trim()?.length !== 0) {
            questionsListCopy = questionsListCopy.map((t) =>
              t.id === builderId ?
                {
                  ...t,
                  questionMessageValueError: true,
                  questionMessageValueLocalError: getUserFriendlyErrorMessage(
                    errorCodes.FIELD_REQUIRED,
                    null,
                    'Question',
                  ),
                }
              : t,
            )
          }

          // If multiple-choice or single-select has invalid options
          if (
            !hasValidOptions &&
            (questionTypeValue === INTAKE_FORM_TYPE_MULTIPLE_CHOICE_TEXT ||
              questionTypeValue === INTAKE_FORM_TYPE_SINGLE_TEXT)
          ) {
            questionsListCopy = questionsListCopy.map((t) =>
              t.id === builderId ?
                {
                  ...t,
                  options: t.options.map((option) =>
                    option?.optionValue?.trim()?.length === 0 ?
                      {
                        ...option,
                        optionLocalError: optionLocalErrorMsgHandler(option),
                        isOptionError: true,
                        isFirstTimeSaveClicked: true,
                      }
                    : option,
                  ),
                }
              : t,
            )
          }

          setFormError('')
          setFormError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
          setIntakeQuestionBuildersList(questionsListCopy)
          return
        }

        setFormError('')
        questionsListCopy = questionsListCopy.map((t) =>
          t.id === builderId ?
            {
              ...t,
              isSaved: true,
              isPreviouslySaved: true,
              previousSavedData: {
                options: t.options,
                message: message.trim(),
                questionTypeValue: t.questionTypeValue,
                fileFormatOptions: t.fileFormatOptions,
                checkboxesValues: t.checkboxesValues,
              },
              message: message.trim(),
            }
          : t,
        )
        setIntakeQuestionBuildersList(questionsListCopy)
      },
      [intakeQuestionBuildersList, isFileFormatDropDownEmptyByBuilderId, setFormError],
    )

    const previouslySavedQuestionCancelBtnPress = useCallback(
      (questionBuilderId: string) => {
        const newIntakeQuestionBuildersList = intakeQuestionBuildersList.map((t) =>
          t.id === questionBuilderId ?
            {
              ...t,
              message: t.previousSavedData.message,
              options: t.previousSavedData.options,
              questionTypeValue: t.previousSavedData.questionTypeValue,
              checkboxesValues: t.previousSavedData.checkboxesValues ?? t.checkboxesValues,
              fileFormatOptions: t.previousSavedData.fileFormatOptions ?? t.fileFormatOptions,
              fileFormatError: false,
              fileFormatLocalError: '',
              questionTypeError: false,
              questionTypeLocalError: '',
              questionMessageValueError: false,
              questionMessageValueLocalError: '',
              isSaved: true,
            }
          : t,
        )
        setIntakeQuestionBuildersList(newIntakeQuestionBuildersList)
      },
      [intakeQuestionBuildersList],
    )

    const onEditSavedQuestion = useCallback(
      (builderId: string) => {
        let questionsListCopy = [...intakeQuestionBuildersList]
        questionsListCopy = questionsListCopy.map((t) =>
          t.id === builderId ?
            {
              ...t,
              isSaved: false,
            }
          : t,
        )
        setIntakeQuestionBuildersList(questionsListCopy)
      },
      [intakeQuestionBuildersList],
    )
    const onDeleteQuestion = useCallback((builderId: string) => {
      setIntakeQuestionBuildersList((list) => list.filter((question) => question.id !== builderId))
    }, [])

    const onDuplicateQuestion = useCallback((builderId: string) => {
      setIntakeQuestionBuildersList((list) => {
        const idx = list.findIndex((q) => q.id === builderId)
        if (idx === -1) return list
        const original = list[idx]
        if (!original) return list
        const duplicate: IntakeQuestionBuilder = {
          ...original,
          id: crypto.randomUUID(),
          IntakeFormQuestionId: undefined,
          isPreviouslySaved: true,
          isSaved: true,
          isPublished: false,
          options: original.options.map((o) => ({ ...o, optionId: crypto.randomUUID() })),
        }
        const copy = [...list]
        copy.splice(idx + 1, 0, duplicate)
        return copy
      })
    }, [])

    const onNewQuestionBtnToolPress = useCallback(
      (initiatorQuestionIndex: number) => {
        const buildersListCopy = [...intakeQuestionBuildersList]
        const newBuilderInstance: IntakeQuestionBuilder = {
          id: crypto.randomUUID(),
          questionTypeValue: '',
          fileFormatOptions: multiSelectFileFormatOptions,
          isPreviouslySaved: false,
          previousSavedData: {
            options: [
              {
                optionId: crypto.randomUUID(),
                optionValue: '',
                optionLocalError: '',
                isOptionError: false,
                isFirstTimeSaveClicked: false,
              },
              {
                optionId: crypto.randomUUID(),
                optionValue: '',
                optionLocalError: '',
                isOptionError: false,
                isFirstTimeSaveClicked: false,
              },
            ],
            message: '',
            questionTypeValue: '',
          },
          message: '',
          checkboxesValues: {
            EnableMultipleAnswers: false,
            isRequired: false,
          },
          options: [
            {
              optionId: crypto.randomUUID(),
              optionValue: '',
              optionLocalError: '',
              isOptionError: false,
              isFirstTimeSaveClicked: false,
            },
            {
              optionId: crypto.randomUUID(),
              optionValue: '',
              optionLocalError: '',
              isOptionError: false,
              isFirstTimeSaveClicked: false,
            },
          ],
          questionTypeError: false,
          questionMessageValueError: false,
          questionMessageValueLocalError: '',
          questionTypeLocalError: '',
        }
        buildersListCopy.splice(initiatorQuestionIndex, 0, newBuilderInstance)
        setIntakeQuestionBuildersList(buildersListCopy)
      },
      [intakeQuestionBuildersList, multiSelectFileFormatOptions],
    )
    const generateNewOptionList = (
      prevList: QuestionOption[],
      newOptionInstance: QuestionOption,
      initiatorOptionIndex: number,
    ): QuestionOption[] => {
      const listCopy = [...prevList]
      listCopy.splice(initiatorOptionIndex + 1, 0, newOptionInstance)
      return listCopy
    }

    const onAddOptionIconPress = useCallback(
      (initiatorOptionIndex: number, builderId: string) => {
        const buildersListCopy = [...intakeQuestionBuildersList]
        const newOptionInstance: QuestionOption = {
          optionId: crypto.randomUUID(),
          optionValue: '',
          optionLocalError: '',
          isOptionError: false,
        }
        const newBuildersList = buildersListCopy.map((t) =>
          t.id === builderId ?
            { ...t, options: generateNewOptionList(t.options, newOptionInstance, initiatorOptionIndex) }
          : t,
        )
        setIntakeQuestionBuildersList(newBuildersList)
      },
      [intakeQuestionBuildersList],
    )

    useEffect(() => {
      if (error === getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, 'Intake form')) {
        // Preserves the original .jsx effect: surfaces the server "name exists" error on the
        // name field.
        setNewIntakeFormNameError(true)
        setNewIntakeFormNameLocalError(getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, 'Intake form'))
      }
    }, [error])

    useEffect(() => {
      if (error) {
        const timeoutId = setTimeout(() => {
          setFormError('')
        }, 3500)
        return () => {
          if (timeoutId) {
            window.clearTimeout(timeoutId)
          }
        }
      }
      return undefined
    }, [error, setFormError])

    const onSetIntakeFormNameValue = useCallback(
      (newValue: string) => {
        setNewIntakeFormNameValue(newValue)
        setFormError('')
        setNewIntakeFormNameError(false)
        setNewIntakeFormNameLocalError('')
      },
      [setFormError],
    )

    // Dirty detection: serialize the current form the same way the save body is built (name +
    // question request objects), ignoring volatile client-only fields (ids, error flags). Snapshot
    // the first-render value and compare — reworks/reverts back to the original read as not-dirty.
    const currentIntakeSnapshot = useMemo(
      () =>
        JSON.stringify({
          Name: newIntakeFormNameValue,
          Questions: intakeQuestionBuildersList.map((question) => generateIntakeFormRequestQuestionObject(question)),
        }),
      [newIntakeFormNameValue, intakeQuestionBuildersList, generateIntakeFormRequestQuestionObject],
    )
    const initialIntakeSnapshotRef = useRef<string | null>(null)
    initialIntakeSnapshotRef.current ??= currentIntakeSnapshot
    const isDirty = initialIntakeSnapshotRef.current !== currentIntakeSnapshot

    // Guard the two exit paths (Cancel + nav-bar) once the user has edited.
    const {
      isPopupOpen: isDiscardPopupOpen,
      handleCancel: onCancelEdit,
      handleDiscard: onDiscardChanges,
      handleKeepEditing: onKeepEditing,
    } = useDiscardChangesGuard({ isDirty, onConfirmLeave: onCancelBtnPress ?? (() => {}) })

    return (
      <div className='flex flex-1 flex-col gap-sp-3 bg-deep-navy-5 px-sp-3 pb-[100px] pt-sp-4'>
        <DiscardChangesPopup
          isOpen={isDiscardPopupOpen}
          entityName={title}
          onDiscard={onDiscardChanges}
          onKeepEditing={onKeepEditing}
        />
        <IntakeBreadcrumbHeader isEdit={isEdit} title={title} onTitleCrumbClick={onCancelEdit} />

        {!isEdit && <IntakeInfoBanner />}

        <IntakeNameCard
          isEdit={isEdit}
          onSetIntakeFormNameValue={onSetIntakeFormNameValue}
          newIntakeFormNameError={newIntakeFormNameError}
          newIntakeFormNameLocalError={newIntakeFormNameLocalError}
          newIntakeFormNameValue={newIntakeFormNameValue}
        />

        <QuestionBuildersList
          intakeQuestionBuildersList={intakeQuestionBuildersList}
          isAllBuildersClosed={isAllBuildersClosed}
          onToggleFollowUp={onToggleFollowUp}
          onChangeFollowUpRequired={onChangeFollowUpRequired}
          onChangeFollowUpText={onChangeFollowUpText}
          onChangeQuestionTypeValue={onChangeQuestionTypeValue}
          onChangeFileFormatValue={onChangeFileFormatValue}
          onChangeQuestionOptionValue={onChangeQuestionOptionValue}
          onChangeQuestionOptionErrorValue={onChangeQuestionOptionErrorValue}
          onChangeQuestionMessageValue={onChangeQuestionMessageValue}
          onChangeCheckBoxesValue={onChangeCheckBoxesValue}
          onBuilderCancelBtnPress={onBuilderCancelBtnPress}
          previouslySavedQuestionCancelBtnPress={previouslySavedQuestionCancelBtnPress}
          onNewQuestionBtnToolPress={onNewQuestionBtnToolPress}
          onAddOptionIconPress={onAddOptionIconPress}
          onBuilderSaveBtnPress={onBuilderSaveBtnPress}
          onEditSavedQuestion={onEditSavedQuestion}
          onDeleteQuestion={onDeleteQuestion}
          onDuplicateQuestion={onDuplicateQuestion}
          onPageCancel={isEdit ? undefined : onCancelEdit}
        />

        {isEdit && onDeleteIntakeForm ?
          <IntakeDeleteSection onDeleteIntakeForm={onDeleteIntakeForm} />
        : null}

        <IntakeActionBar isEdit={isEdit} onCancelBtnPress={onCancelEdit} onSaveBtnPress={onSaveBtnPress} />
      </div>
    )
  },
)

export default CreateEditIntakeView
